import { Fragment, useCallback, useState, useMemo, ReactNode } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import Flag from '@atlaskit/flag';
import { ActionType } from '@atlaskit/flag/types';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { token } from '@atlaskit/tokens';

import { getEditorState } from 'src/redux/pull-request/selectors';
import DiscardCommentsDialog from 'src/sections/repository/sections/pull-request/components/discard-comments-dialog';
import { messages } from 'src/sections/repository/sections/pull-request/pull-request.i18n';

import { AppearanceTypes } from './simple/simple-flag';

type BasePullRequestUpdatedFlagProps = {
  id: string;
  title: string;
  description?: string;
  onRefresh: () => void;
  handleDismissed: (id: string) => void;
  children?: ReactNode;
};

export const BasePullRequestUpdatedFlag = ({
  id,
  title,
  description,
  onRefresh,
  handleDismissed,
  children,
  ...flagProps
}: BasePullRequestUpdatedFlagProps) => {
  const isEditorOpen = useSelector(getEditorState);
  const [showDiscardCommentsDialog, setShowDiscardCommentsDialog] =
    useState(false);
  const intl = useIntl();

  const handleUpdate = useCallback(() => {
    onRefresh();
    handleDismissed(id);
  }, [id, handleDismissed, onRefresh]);

  const refreshAction: ActionType = useMemo(
    () => ({
      content: intl.formatMessage(messages.reload),
      onClick: () => {
        if (isEditorOpen) {
          setShowDiscardCommentsDialog(true);
        } else {
          handleUpdate();
        }
      },
    }),
    [handleUpdate, intl, isEditorOpen]
  );

  const handleDiscardComments = useCallback(() => {
    setShowDiscardCommentsDialog(false);
    handleUpdate();
  }, [handleUpdate]);

  const handleDiscardCommentsCancel = useCallback(() => {
    setShowDiscardCommentsDialog(false);
  }, []);

  return (
    <Fragment>
      <Flag
        id={id}
        title={title}
        appearance={AppearanceTypes.normal}
        description={description}
        icon={
          <StatusInformationIcon
            spacing="spacious"
            label=""
            color={token('color.icon.discovery')}
          />
        }
        actions={[refreshAction]}
        {...flagProps}
      />
      {showDiscardCommentsDialog && (
        <DiscardCommentsDialog
          onCancel={handleDiscardCommentsCancel}
          onDiscard={handleDiscardComments}
        >
          <FormattedMessage
            {...messages.discardCommentsModalBody}
            tagName="p"
          />
        </DiscardCommentsDialog>
      )}
      {children}
    </Fragment>
  );
};
